// Shared (server + client) contract for the storefront light/dark mode.
//
// The mode lives on <html> as `data-theme` (our tokens) + `data-bs-theme` (Bootstrap's
// own colour mode, so modals/dropdowns/form controls flip with no extra CSS). Only the
// pre-paint boot script and `themeModeStore` write these attributes.

export type ThemeMode = "light" | "dark";

// Deliberately the plain `theme` key: tenants that hand-rolled dark mode through custom
// header scripts (e.g. bybravura.com) already store `"light" | "dark"` under it, so their
// shoppers keep their choice when the merchant switches to the built-in feature. Values
// are validated on read, so an unrelated script using the same key just reads as "unset".
export const THEME_STORAGE_KEY = "theme";
export const THEME_ATTR = "data-theme";
export const BS_THEME_ATTR = "data-bs-theme";
export const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === "light" || value === "dark";

/**
 * Inline script for <head>, rendered ONLY when the tenant enabled dark mode. It runs
 * before first paint so a returning dark-mode shopper never sees a light flash (and
 * vice versa). Kept dependency-free and ES5-safe; storage access is wrapped because it
 * throws in some in-app browsers / blocked-storage modes — we then follow the device.
 */
export const getThemeBootScript = () =>
  `(function(){try{var d=document.documentElement,t=null;try{t=localStorage.getItem(${JSON.stringify(
    THEME_STORAGE_KEY,
  )})}catch(e){}if(t!=="light"&&t!=="dark"){t=window.matchMedia&&window.matchMedia(${JSON.stringify(
    DARK_MEDIA_QUERY,
  )}).matches?"dark":"light"}d.setAttribute(${JSON.stringify(
    THEME_ATTR,
  )},t);d.setAttribute(${JSON.stringify(BS_THEME_ATTR)},t)}catch(e){}})()`;
